import { useNavigation } from '@react-navigation/native'
import { links, platform } from 'constants'
import { PostTypes } from 'constants/postTypes'
import React, { useEffect, useMemo, useState } from 'react'
import { StyleSheet } from 'react-native'

import { DeletePostModal } from 'partials/DeletePostModal'
import { ActionIcon } from 'partials/PostCard/subcomponents'
import { MediaPlayer } from 'partials/PostPlayer/widgets/MediaPlayer'

import { Box } from 'widgets/Box'
import { Card } from 'widgets/Card'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { DraftOverlay } from 'widgets/PostOverlay'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'

import { useAspectRatio } from 'hooks/useAspectRatio'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { useLike } from 'hooks/useLike'
import { usePendingMembership } from 'hooks/usePendingMembership'
import { usePost } from 'hooks/usePost'

import { showPendingMembershipSuccess } from 'utils/functions/notifications'
import { handlePostClicked } from 'utils/functions/posts'
import { generateDynamicLink } from 'utils/links'
import { copyLinkToClipboard, getPostLink } from 'utils/links/links'
import { defaultPostThumbnailUrl, getFileTypeFromMimeType } from 'utils/media'
import commonStyles from 'utils/styles/commonStyles'
import { timeSince } from 'utils/time'

import { useTheme } from 'contexts/ThemeContext'

import { Post } from 'types/post'
import { Program } from 'types/program'

interface GridItemProps {
  post: Post
  isLast?: boolean
  isCompact?: boolean
  isUpspaceItem?: boolean
  width?: number
  height?: number
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  postPressable: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
})

export const GridItem = ({
  post,
  isLast = false,
  isCompact,
  isUpspaceItem,
  width,
  height,
}: GridItemProps) => {
  const { data: fullPost } = usePost(post.id)
  const { created, creator, id, isDraft, isPremium, title, likedCount, type } = post
  const isWorkout = useMemo(() => type === PostTypes.WORKOUT, [type])
  const navigation = useNavigation<any>()
  const { data: user } = useCurrentUser()

  const {
    data: isPostLiked,
    likePost,
    dislikePost,
  } = useLike({
    postId: id,
    userId: user?.id,
  })
  const isLiked = isPostLiked && likedCount > 0
  const theme = useTheme()
  const isOwner = user?.id === creator?.id

  const scaledHeight = useAspectRatio({
    aspectRatioOffset: (theme.spacing.sm + theme.border.sm) * 2,
    dimension: 'height',
    aspectRatio: isUpspaceItem ? 2.0 : 1.65,
  })

  const [postLink, setPostLink] = useState('')
  useEffect(() => {
    generateDynamicLink(getPostLink(post.id, post.type)).then((dynamicLink) =>
      setPostLink(dynamicLink),
    )
  }, [post?.id, post.type])

  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const menuOptions = ['Edit', 'Duplicate', 'Delete']

  if (!isDraft) {
    menuOptions.push('Copy post link')
  }

  const handleCloseOptionsMenuPress = () => {
    setIsOptionsModalVisible(false)
  }

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)
    if (index === 0) {
      handleEditPost()
    } else if (index === 1) {
      handleDuplicatePost()
    } else if (index === 2) {
      openDeleteModal()
    } else if (index === 3) {
      copyLinkToClipboard(postLink)
    }
  }

  const handlePostOptionsMenu = () => {
    setIsOptionsModalVisible(true)
  }

  const handleEditPost = () => {
    navigation.navigate(links.EDIT_WORKOUT_PAGE, {
      postId: id,
    })
  }

  const handleDuplicatePost = () => {
    navigation.navigate(links.CREATE_WORKOUT_PAGE, {
      postId: id,
    })
  }

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const handleLikeClicked = () => {
    if (isLiked) {
      dislikePost.mutateAsync({ postId: post.id, user })
    } else {
      likePost.mutateAsync({ postId: post.id, user })
    }
  }

  const { addPendingMembership } = usePendingMembership({
    userId: user?.id,
    programId: post?.program?.id,
  })

  const { data: postGroup } = useGroup({ groupId: post?.program?.id })
  const handleAddPendingMembership = () => {
    addPendingMembership.mutate()
    showPendingMembershipSuccess(post, postGroup as Program)
  }

  let firstStepFile
  if (!isWorkout && fullPost?.steps?.length) {
    const steps = fullPost?.steps?.filter((step) => step.file !== null)
    firstStepFile = steps[0].file
  }

  return (
    <>
      <DeletePostModal handleClose={closeDeleteModal} isOpen={isDeleteModalOpen} postId={id} />
      <Box
        testID={`postCard${post.id}`}
        style={isUpspaceItem ? undefined : { flex: isCompact ? 0.33 : 0.5 }}
        width={width ?? isUpspaceItem ? scaledHeight * 0.8 : undefined}
        flexDirection={isUpspaceItem ? 'row' : undefined}
      >
        <Card
          flex={isUpspaceItem ? 1 : undefined}
          height={height ?? scaledHeight * (isCompact ? 0.7 : 1)}
          flexDirection='column'
          justifyContent='space-between'
          margin={{
            horizontal: isCompact ? 'xxs' : 'sm',
            top: isCompact ? 'xs' : 'md',
            bottom: platform.IS_IOS && isLast ? 'xl' : 0,
          }}
        >
          <Box
            testID='BackgroundImageContainer'
            zIndex={1}
            position='absolute'
            display='flex'
            width='100%'
            height='100%'
            backgroundColor='greyLight'
          >
            {isDraft && <DraftOverlay />}

            {isWorkout && (
              <Image
                cacheKey={`Post-${id}-thumb`}
                style={commonStyles.fullSize}
                source={{ uri: post?.thumbnail?.url ?? defaultPostThumbnailUrl.url }}
              />
            )}

            {!isWorkout && (
              <Box flex={1}>
                <MediaPlayer
                  file={firstStepFile}
                  autoplay={false}
                  muted
                  hideVideoPlayPauseButton={isCompact}
                  isPreview
                />
              </Box>
            )}

            {isCompact && (
              <Box
                width='100%'
                height='100%'
                position='absolute'
                padding='sm'
                pointerEvents='none'
                align='flex-start'
                justifyContent='flex-end'
              >
                {!isWorkout && getFileTypeFromMimeType(firstStepFile?.mimeType) === 'video' ? (
                  <Icon name='play-rounded' color='white' />
                ) : (
                  <Text bold fontSize='sm' color='white' style={commonStyles.shadowText}>
                    {title}
                  </Text>
                )}
              </Box>
            )}

            <Pressable
              style={styles.postPressable}
              onPress={() =>
                handlePostClicked(isOwner, navigation, () => handleAddPendingMembership, post)
              }
            />
          </Box>

          {!isCompact && (
            <>
              <Box
                testID='TopContentContainer'
                pointerEvents='box-none'
                zIndex={2}
                margin={{ top: 'sm', horizontal: 'smd' }}
                flexDirection='row'
                align='center'
                justifyContent='space-between'
              >
                <Box pointerEvents='none' justifyContent='center'>
                  <Text
                    bold
                    fontSize='xs'
                    align='right'
                    color='white'
                    style={commonStyles.shadowText}
                  >
                    {timeSince(created)}
                  </Text>
                </Box>
              </Box>

              <Box
                testID='BottomContentContainer'
                zIndex={2}
                padding={{ horizontal: 'xs', bottom: 'sm' }}
                flexDirection='row'
                align='flex-end'
                justifyContent='space-between'
              >
                <Box padding={{ left: 'sm' }} flex={1}>
                  <Text align='left' fontSize='sm' bold color='textInverted' numberOfLines={3}>
                    {title}
                  </Text>
                </Box>

                <Box padding={{ horizontal: 'sm' }} pointerEvents='box-none'>
                  {isOwner ? (
                    <Box pointerEvents='auto' align='flex-start'>
                      <ActionIcon
                        icon='options-menu'
                        label=''
                        direction='row'
                        action={handlePostOptionsMenu}
                      />
                    </Box>
                  ) : (
                    <ActionIcon
                      direction='row'
                      testID={`likeButton${isLiked ? 'On' : 'Off'}`}
                      icon={isLiked ? 'heart-filled' : 'heart'}
                      label={likedCount.toString()}
                      action={handleLikeClicked}
                      disabled={likePost.isLoading || dislikePost.isLoading}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
        </Card>
      </Box>
      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>
    </>
  )
}
